import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'

import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { SkeletonRows } from '@/components/ui/skeleton'
import { DriversTable } from '../../../components/DriversTable'
import { FleetStatusBanner } from '../../../components/FleetStatusBanner'
import { QuotesLockNotice } from '../../../components/QuotesLockNotice'
import { type Driver, useDrivers } from '../../../models/driver'
import {
  continuesApplication,
  FleetEditMode,
  rosterEditMode,
  showsQuotesLock,
  useCurrentUser,
} from '../../../models/user'
import { useRosterChangePrompt } from './PolicyChanges/useRosterChangePrompt'

export function Drivers() {
  const { user } = useCurrentUser({ enabled: true })
  const orgId = user?.currentOrg.id
  const { drivers, isLoading } = useDrivers({ orgId, shouldFetchAllPages: true })
  const [selectedDrivers, setSelectedDrivers] = useState<Driver[]>([])
  const quotesLocked = showsQuotesLock(user, user?.currentOrg.currentInsuranceApplication)
  const mode = rosterEditMode(user, quotesLocked)
  const readOnly = mode === FleetEditMode.LOCKED
  // Under a policy in force, a change here waits for review in a policy change request.
  const { prompt, afterRosterChange } = useRosterChangePrompt(
    mode === FleetEditMode.REVIEWED ? orgId : undefined
  )

  useEffect(() => {
    setSelectedDrivers(drivers)
  }, [drivers])

  function renderContent() {
    if (isLoading || !orgId) {
      return <SkeletonRows />
    }

    return (
      <>
        {quotesLocked && <QuotesLockNotice section="drivers" />}
        {!quotesLocked && mode !== FleetEditMode.EDITABLE && (
          <FleetStatusBanner label="drivers" mode={mode} status={user.currentOrg.status} />
        )}
        <DriversTable
          drivers={selectedDrivers}
          setDrivers={setSelectedDrivers}
          orgId={orgId}
          hideHeading
          changesDisabled={continuesApplication(user)}
          addCtaOnTop
          searchable
          readOnly={readOnly}
          onAdd={afterRosterChange}
          onEdit={afterRosterChange}
          onDelete={afterRosterChange}
          onRestore={afterRosterChange}
          lockBusinessOwner={mode === FleetEditMode.REVIEWED}
        />
        {prompt}
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Drivers</title>
      </Helmet>
      <h1 className="text-ink mb-4 text-2xl font-bold tracking-[-0.01em]">Drivers</h1>
      <OnboardingBannerSlot className="mb-4" />
      <div className="flex flex-col gap-5">{renderContent()}</div>
    </>
  )
}
